/**
 * SnackbarMessage
 * https://elsevier.atlassian.net/browse/OSMS-303
 * Reusable component to display a message via the Snackbar.
 */

import React, { ReactNode, useEffect, useState } from 'react';

import {
  bgNegative,
  bgPositive,
  bgPrimary,
  bgWarning,
} from '@osmosis/tailwind-ui/styles/background';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { textGrayInvert } from '@osmosis/tailwind-ui/styles';

const variantMap: Record<string, string> = {
  positive: bgPositive,
  negative: bgNegative,
  primary: bgPrimary,
  warning: bgWarning,
};

type Props = {
  message: ReactNode;
  icon?: ReactNode;
  button?: ReactNode;
  cn?: string;
  variant?: string;
  /** Role for the live region that announces the message. */
  role?: React.AriaRole;
};

/**
 * SnackbarMessage.
 */
export const SnackbarMessage = (props: Props) => {
  const {
    message,
    variant = 'bg-transparent',
    button,
    icon,
    cn,
    role = 'status',
  } = props;

  const [liveMessage, setLiveMessage] = useState<ReactNode>(null);

  useEffect(() => {
    setLiveMessage(null);
    const timer = setTimeout(() => {
      setLiveMessage(message);
    }, 100);

    return () => clearTimeout(timer);
  }, [message]);

  const bgColor = variantMap[variant] || variant;

  return (
    <Flex className={`py-3 pl-4 pr-3 ${bgColor} gap-2 rounded-[5px]`} cn={cn}>
      <Flex className="flex-row items-center gap-2">
        <Box className={textGrayInvert} data-testid="snackbar-message">
          {message}
        </Box>
        {button}
      </Flex>
      {icon}

      {/*
       * This persistent sr-only node is the snackbar's only live region: it is
       * mounted before `liveMessage` is populated (100ms above) so the text
       * arrives as a mutation, which is what actually triggers an
       * announcement. Do not add a second live region to an ancestor — the
       * outer wrapper is inserted with its content already present, so it
       * would not reliably announce, and where it did it would announce the
       * whole atomic subtree (message + action + close) on top of this one.
       */}
      <Box
        role={role}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMessage}
      </Box>
    </Flex>
  );
};
